import { billingTypeEnum, tripDutyTypeEnum } from "utils/enumObj";
import {
  formatCurrency,
  formatNumber,
  getDateFormat,
  getTimeFormat,
  convertDecimalHoursToTime
} from "utils/utils";

// Helper function to calculate garage charges
const getGarageCharges = (tripDetails) => {
  const breakdown = tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown || [];
  const garageCharge = breakdown.find(item => item?.chargeType === 'garageCharge') || {};
  const garageBreakup = garageCharge?.meta?.breakup || [];
  
  const garageToPickup = parseFloat(garageBreakup[0]?.rate || 0);
  const dropToGarage = parseFloat(garageBreakup[1]?.rate || 0);
  
  return garageToPickup + dropToGarage;
};

// Helper function to calculate Sub Total A (base fare + extra charges + surcharges + platform charge)
const calculateSubTotalA = (tripDetails) => {
  const tripPricing = tripDetails?.Payments?.[0]?.tripPricingDetails || {};
  const baseFare = tripDetails?.Payments?.[0]?.tripPricingDetails?.baseFareDisplay || 0;
  const baseRate = parseFloat(tripPricing?.baseRate || 0);
  const surCharge = parseFloat(tripPricing?.surCharge || 0);
  const platformCharge = parseFloat(tripPricing?.platformCharge || 0);
  const extraKmCharges = tripDetails?.type === tripDutyTypeEnum?.rental 
    ? parseFloat(tripDetails?.extraKmCharges || 0)
    : parseFloat(tripDetails?.extraPrice || 0);
  const extraHrCharges = parseFloat(tripDetails?.extraHrCharges || 0);
  
  return baseFare + baseRate + surCharge + platformCharge + extraKmCharges + extraHrCharges;
};

// Helper function to calculate Sub Total B (all additional charges)
const calculateSubTotalB = (tripDetails) => {
  const breakdown = tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown || [];
  const driverAllowance = parseFloat(breakdown.find(b => b.chargeType === "driverAllowance")?.addedCharge || 0);
  const handlingCharges = parseFloat(tripDetails?.handlingCharges || 0);
  const nightAllowance = parseFloat(breakdown.find(b => b.chargeType === "nightAllowance")?.addedCharge || 0);
  const fuelSurcharge = parseFloat(tripDetails?.fuelSurcharge || 0);
  const interstateTax = parseFloat(tripDetails?.interstateTaxCharges || 0);
  const placardCharge = parseFloat(breakdown.find(b => b.chargeType === "placard")?.addedCharge || 0);
  const garageCharge = parseFloat(breakdown.find(b => b.chargeType === "garageCharge")?.addedCharge || 0);
  const additionalCharges = parseFloat(breakdown.find(b => b.chargeType === "additionalCharges")?.addedCharge || 0);
  const couponDiscount = Math.abs(parseFloat(breakdown.find(b => b.chargeType === "discountAmount")?.addedCharge || 0));
  const tollCharges = parseFloat(breakdown.find(b => b.chargeType === "tollCharges")?.addedCharge || 0);
  const parkingCharges = parseFloat(breakdown.find(b => b.chargeType === "parkingCharge")?.addedCharge || 0);
  
  return tollCharges + parkingCharges + driverAllowance + handlingCharges + nightAllowance + fuelSurcharge + interstateTax + placardCharge + garageCharge + additionalCharges - couponDiscount;
};

export const tripDutySlipData = (tripDetails, tripTypeDetails) => {
  // Add null check for tripDetails
  if (!tripDetails) {
    return {
      detailsArray1: [],
      detailsArray2: [],
      detailsArray3: [],
      detailsArray4: [],
      detailsArray5: [],
      detailsArray6: []
    };
  }

  const guestPassengers = tripDetails?.additionalInfo?.passengersDetails?.guestPassengers || [];
  const payments = tripDetails?.Payments || [];
  const payment = payments[0] || {};
  const tripPricingDetails = payment?.tripPricingDetails || {};
  const rateConfig = tripPricingDetails?.rateConfig || {};
  const breakdown = tripPricingDetails?.breakdown || [];

  // Safe access for nested objects
  const garageCharge = breakdown.find(item => item?.chargeType === 'garageCharge') || {};
  const garageBreakup = garageCharge?.meta?.breakup || [];

  // Create guest details entries 
  const guestDetailsTable = guestPassengers.length > 0 ? {
    title: "Guest Details",
    tableHeaders: ["Guest Name", "Phone Number"],
    tableData: guestPassengers.map((guest, index) => ({
      id: index + 1,
      name: guest?.name || "",
      phone: guest?.countryCode ? `${guest.countryCode} ${guest.phone}` : guest?.phone || "",
    }))
  } : null;

  return {
    detailsArray1: [
      {
        title: "Service",
        value: (tripDetails?.dutyTypeName || "") + (tripDetails?.additionalInfo?.isMultiDay ? " (Multi-Day)" : ""),
      },
      {
        title: "Company Name",
        value: tripDetails?.companyName || "",
      },
      {
        title: "Passenger",
        value: tripDetails?.customerName || "",
      },
      {
        title: "Mobile No.",
        value: tripDetails?.customerPhone || "",
      },
      {
        title: "Booked By",
        value: tripDetails?.TripBookedBy?.name || "",
      },
      {
        title: "No. Of Guest",
        value: (tripDetails?.additionalInfo?.passengersDetails?.numberOfPassengers - 1) || "",
      },
      ...(guestDetailsTable ? [{
        title: "Guest Details",
        isTable: true,
        tableData: guestDetailsTable
      }] : []),
    ],
    detailsArray2: [
      {
        title: "Submission City",
        value: tripDetails?.ServiceCity?.name || "",
      },
      {
        title: "DS No. / Booking ID",
        value: tripDetails?.tripId || "",
      },
      {
        title: "Reporting Address",
        value: tripDetails?.sourceAddress || "",
      },
      {
        title: "Package",
        value: (tripDetails?.type === tripDutyTypeEnum.rental || tripDetails?.type === tripDutyTypeEnum.roundTrip) 
          ? rateConfig?.name 
          : 'Per KM',
      },
      {
        title: "Garage Location",
        value: (tripDetails?.tripBranch?.additionalInfo?.garageAddress)
      }
    ],
    detailsArray3: [
      {
        title: "Date Out",
        value: tripDetails?.pickUpTime ? getDateFormat(tripDetails?.pickUpTime) : "",
      },
      {
        title: "Date In",
        value: tripDetails?.completedAt ?getDateFormat(tripDetails?.completedAt) : "",
      },
      {
        title: "Total Days",
        value: payment?.tripPricingDetails?.rateConfig?.durationType === 'day' 
          ? payment?.tripPricingDetails?.rateConfig?.duration 
          : "",
      },
      {
        title: "Time Out",
        value: getTimeFormat(tripDetails?.pickUpTime) || "",
      },
      {
        title: "Time In",
        value: getTimeFormat(tripDetails?.completedAt) || "",
      },
      {
        title: "Total hr",
        value: payment?.tripPricingDetails?.rateConfig?.durationType === 'day'
          ? payment?.tripPricingDetails?.rateConfig?.duration
          : convertDecimalHoursToTime(tripDetails?.totalTime) || "",
      },
      {
        title: "km Out",
        value: 0,
      },
      {
        title: "km In",
        value: `${tripDetails?.distanceActual || ""}`,
      },
      {
        title: "Total km",
        value: tripDetails?.type === tripDutyTypeEnum.rental ? tripDetails?.Payments[0]?.tripPricingDetails?.rateConfig?.maxDistanceKms : `${tripDetails?.distanceActual || ''}`,
      },
    ],
    detailsArray4: [
      {
        title: "Garage to Pickup",
        value: `${tripDetails?.additionalInfo?.kilometersOut ? tripDetails?.additionalInfo?.kilometersOut + ' km' : '0.00 km'}`,
      },
      {
        title: "Pickup to Drop",
        value: `${tripDetails?.distanceActual? tripDetails?.distanceActual + ' km' : '0.00 km'}`,
      },
      {
        title: "Drop to Garage",
        value: `${tripDetails?.additionalInfo?.kilometersIn ? tripDetails?.additionalInfo?.kilometersIn + ' km' : '0.00 km'}`,
      },
      {
        title: "Garage to Pickup Charge",
        value: `₹${formatNumber(parseFloat(garageBreakup[0]?.rate || 0))}`,
      },
      {
        title: "Drop to Garage Charge",
        value: `₹${formatNumber(parseFloat(garageBreakup[1]?.rate || 0))}`,
      },
    ],
    detailsArray5: [
      {
        title: "Rep Time",
        value: getTimeFormat(tripDetails?.scheduledTime) || "",
      },
      {
        title: "Driver",
        value: tripDetails?.DriverProfile?.driverName || "",
      },
      {
        title: "Mobile No.",
        value: tripDetails?.DriverProfile?.mobile || "",
      },
      {
        title: "Car Booked",
        value: tripDetails?.MasterCarType?.carType || "",
      },
      {
        title: "Car Alloted",
        value: tripDetails?.DriverProfile?.Vehicle?.carType || "",
      },
      {
        title: "Car No.",
        value: tripDetails?.DriverProfile?.Vehicle?.regNumber || "",
      },
    ],
    detailsArray6: [
      {
        title: "Opening hr",
        value: getTimeFormat(tripDetails?.pickUpTime) || "",
      },
      {
        title: "Closing hr",
        value: getTimeFormat(tripDetails?.completedAt) || "",
      },
      {
        title: "Opening km",
        value: 0,
      },
      {
        title: "Closing km",
        value:  `${tripDetails?.distanceActual || ""}`,
      },
    ],
  };
};

export const tripPerformaInvoiceData = (tripDetails, tripTypeDetails, subTotalA, subTotalB, totalAB, totalGstAmount,grossTotal) => { 
  const tripPricing = tripDetails?.Payments?.[0]?.tripPricingDetails || {};
  const baseRate = parseFloat(tripPricing?.baseRate || 0);
  const surCharge = parseFloat(tripPricing?.surCharge || 0);
  const platformCharge = parseFloat(tripPricing?.platformCharge || 0);
  const breakdown = tripPricing?.breakdown || [];
  const extraKmCharges = tripDetails?.type === tripDutyTypeEnum?.rental 
    ? parseFloat(tripDetails?.extraKmCharges || 0)
    : parseFloat(tripDetails?.extraPrice || 0);
  const extraHrCharges = parseFloat(tripDetails?.extraHrCharges || 0);
  const driverAllowance = parseFloat(breakdown.find(b => b.chargeType === "driverAllowance")?.addedCharge || 0);
  const handlingCharges = parseFloat(tripDetails?.handlingCharges || 0);
  const nightAllowance = parseFloat(breakdown.find(b => b.chargeType === "nightAllowance")?.addedCharge || 0);
  const fixedGarageRate = parseFloat(tripTypeDetails?.fixedGarageRate || 0);
  const garageCharge = parseFloat(breakdown.find(b => b.chargeType === "garageCharge")?.addedCharge || 0);
  const fuelSurcharge = parseFloat(tripDetails?.fuelSurcharge || 0);
  const interstateTax = parseFloat(tripDetails?.interstateTaxCharges || 0);
  const tollCharges = Number(tripDetails?.Payments[0]?.tripPricingDetails?.breakdown?.find((item) => item.chargeType === "tollCharges")?.addedCharge ?? 0);
  const parkingCharges = Number(tripDetails?.Payments[0]?.tripPricingDetails?.breakdown?.find((item) => item.chargeType === "parkingCharge")?.addedCharge ?? 0);
  const additionalCharges = parseFloat(breakdown.find(b => b.chargeType === "additionalCharges")?.addedCharge || 0);
  const placardCharge = parseFloat(breakdown.find(b => b.chargeType === "placard")?.addedCharge || 0);
  const couponDiscount = Math.abs(parseFloat(breakdown.find(b => b.chargeType === "discountAmount")?.addedCharge || 0));
  
  // Build detailsArray4 with only non-zero values
  const detailsArray4Base = [
    {
      title: "Service Type",
      value: (tripDetails?.dutyTypeName || "") + (tripDetails?.additionalInfo?.isMultiDay ? " (Multi-Day)" : ""),
    },
    {
      title: "Package",
      value: (tripDetails?.type === tripDutyTypeEnum.rental || tripDetails?.type === tripDutyTypeEnum.roundTrip) ? tripDetails?.Payments[0]?.tripPricingDetails?.rateConfig?.name : 'Per KM',
    },
    {
      title: "Package Rate",
      value: `₹${formatNumber(tripDetails?.Payments[0]?.tripPricingDetails?.baseFareDisplay || 0)}`,
    },
    {
      title: "km Used",
      value: `${formatNumber(parseFloat(tripDetails?.distanceActual || "0") + parseFloat(tripDetails?.extraKm || "0"))} `
    },
    ...(extraKmCharges > 0 ? [{
      title: "Extra km",
      value: `${formatNumber(tripDetails?.extraKm) || ""}`,
    },
    {
      title: "Extra km Rs",
      value: tripDetails?.type === tripDutyTypeEnum?.rental ? `₹${formatNumber(tripDetails?.extraKmCharges)}` : `₹${formatNumber(tripDetails?.extraPrice)}`,
    }] : []),
    {
      title: "hr Used",
      value: convertDecimalHoursToTime(tripDetails?.totalTime) || "",
    },
    ...(extraHrCharges > 0 ? [{
      title: "Extra hr",
      value: tripDetails?.extraTime ? parseFloat(tripDetails?.extraTime)?.toFixed(2) : "",
    },
    {
      title: "Extra hr Rs",
      value: tripDetails?.type === tripDutyTypeEnum?.rental ? `₹${formatNumber(tripDetails?.extraHrCharges || 0)}` : "0.00"
    }] : []),
    {
      title: "Garage To Pickup",
      value: tripDetails?.Payments[0]?.tripPricingDetails?.kilometersOut || 'NA',
    },
    {
      title: "Drop To Garage",
      value: tripDetails?.Payments[0]?.tripPricingDetails?.kilometersIn || 'NA'
    },
    {
      title: "Car Booked",
      value: tripDetails?.MasterCarType?.carType || "",
    },
    {
      title: "Car Used On",
      value: tripDetails?.pickUpTime ? getDateFormat(tripDetails?.pickUpTime) : "",
    },
    ...(baseRate > 0 ? [{
      title: "Base Rate",
      value: `₹${formatNumber(baseRate)}`,
    }] : []),
    ...(surCharge > 0 ? [{
      title: "Surcharge",
      value: `₹${formatNumber(surCharge)}`,
    }] : []),
    ...(platformCharge > 0 ? [{
      title: "Platform Charge",
      value: `₹${formatNumber(platformCharge)}`,
    }] : []),
  ];

  // Sub Total A summary section
  const detailsArray4Summary = [
    {
      title: "",
      value: ""
    },
    {
      title: "",
      value: ""
    },
    {
      title: "Sub Total(A)",
      bold: true,
      value: `₹${formatNumber(parseFloat(calculateSubTotalA(tripDetails)) || 0)}` || "",
    },
  ];

  // Build detailsArray5 with only non-zero values
  const detailsArray5Base = [
    ...(tollCharges > 0 > 0 ? [{
      title: "Toll Charges",
      value: `₹${formatNumber(tollCharges)}`,
    }] : []),
    ...(parkingCharges > 0 ? [{
      title: "Parking Charges",
      value: `₹${formatNumber(parkingCharges)}`,
    }] : []),
    ...(driverAllowance > 0 ? [{
      title: "Driver Allowance",
      value: `₹${formatNumber(driverAllowance)}` || "",
    }] : []),
    ...(handlingCharges > 0 ? [{
      title: "Additional Charges",
      value: `₹${formatNumber(handlingCharges)}` || "",
    }] : []),
    ...(nightAllowance > 0 ? [{
      title: "Night Allowance",
      value: `₹${formatNumber(nightAllowance)}`,
    }] : []),
    ...(fixedGarageRate > 0 ? [{
      title: "Fix Garage Run",
      value: `₹${formatNumber(fixedGarageRate)}`,
    }] : []),
    ...(garageCharge > 0 ? [{
      title: "Garage To Garage Run",
      value: `₹${formatNumber(garageCharge)}`,
    }] : []),
    ...(additionalCharges > 0 ? [{
      title: "Additional Charges",
      value: `₹${formatNumber(additionalCharges)}` || "",
    }] : []),
    ...(placardCharge > 0 ? [{
      title: "Placard Charge",
      value: `₹${formatNumber(placardCharge)}`,
    }] : []),
    ...(fuelSurcharge > 0 ? [{
      title: "Fuel Surcharge",
      value: `₹${formatNumber(fuelSurcharge)}` || ''
    }] : []),
    ...(interstateTax > 0 ? [{
      title: "Interstate Tax",
      value: `₹${formatNumber(interstateTax)}`,
    }] : []),
    ...(couponDiscount > 0 ? [{
      title: "Coupon Discount",
      value: `-₹${formatNumber(couponDiscount)}`,
    }] : []),
  ];

  // Sub Total B summary section
  const detailsArray5Summary = [
    {
      title: "",
      value: ""
    },
    {
      title: "",
      value: ""
    },
    {
      title: "Sub Total (B)",
      bold: true,
      value: `₹${formatNumber(parseFloat(calculateSubTotalB(tripDetails) || 0))}` || "",
    },
  ];

  return {
    detailsArray1: [
      {
        title: "Company Name",
        value: tripDetails?.companyName || "",
      },
      // {
      //   title: "Company Code",
      //   value: "",
      // },
      {
        title: "Company Address",
        value:  tripDetails?.tripCompany?.address || "",
      },
      {
        title: "Passenger Name",
        value: tripDetails?.customerName || "",
      },
      {
        title: "Booked By",
        value: tripDetails?.TripBookedBy?.name || "",
      },
      {
        title: "GSTIN of Company",
        value: tripDetails?.tripBranch?.gstNumber || "",
      },
      {
        title: "Travel Request No.",
        value: tripDetails?.travelRequestNumber || "",
      },
      {
        title: "PAN",
        value: tripDetails?.tripCompany?.panNumber || "",
      }
    ],
    detailsArray2: [
      {
        title: "Billing Type",
        value:
          Number(tripDetails?.additionalInfo?.billingType) ===
          billingTypeEnum?.billToCreditCard
            ? "Bill To Credit Card"
            : Number(tripDetails?.additionalInfo?.billingType) ===
              billingTypeEnum?.billToCompany
            ? "Bill To Company"
            : "",
      },
      {
        title: "DS No. / Booking ID",
        value: tripDetails?.tripId || "",
      },
      {
        title: "Invoice Date",
        value: getDateFormat(tripDetails?.Invoices?.[0]?.createdAt) || "",
      },
      {
        title: "Submission City",
        value: tripDetails?.ServiceCity?.name || "",
      },
      {
        title: "Place Of Supply",
        value: tripDetails?.tripBranch?.gstNumber || tripDetails?.tripBranch?.state,
      },
      {
        title: "Garage Location",
        value: (tripDetails?.tripBranch?.additionalInfo?.garageAddress)
      }
    ],
    detailsArray3: [
      {
        title: "Date Out",
        value: tripDetails?.pickUpTime ? getDateFormat(tripDetails?.pickUpTime) : "",
      },
      {
        title: "Date In",
        value: tripDetails?.completedAt ?getDateFormat(tripDetails?.completedAt) : "",
      },
      {
        title: "Start km",
        value: 0,
      },
      {
        title: "Time Out",
        value: getTimeFormat(tripDetails?.pickUpTime) || "",
      },
      {
        title: "Time In",
        value: getTimeFormat(tripDetails?.completedAt) || "",
      },
      {
        title: "End km",
        value: `${tripDetails?.distanceActual || ""}`,
      },
      {
        title: "Driver Name",
        value: tripDetails?.DriverProfile?.driverName || "",
      },
      {
        title: "Driver Phone No.",
        value: tripDetails?.DriverProfile?.mobile || "",
      },
      {
        title: "Reporting Time",
        value: getTimeFormat(tripDetails?.scheduledTime) || "",
      },
      {
        title: "Car No.",
        value: tripDetails?.DriverProfile?.Vehicle?.regNumber || "",
      },
      {
        title: "Car Alloted",
        value: tripDetails?.DriverProfile?.Vehicle?.carType || "",
      },
    ],
    detailsArray4: detailsArray4Base,
    detailsArray4Summary: detailsArray4Summary,
    detailsArray5: detailsArray5Base,
    detailsArray5Summary: detailsArray5Summary,
    detailsArray6: tripDetails?.termsAndConditions,
    detailsArray7: [
      {
        title: tripDetails?.status  ==='Cancelled' ? 'Sub Total (C)': "Gross Amount(A+B)",
        bold:true,
        value: `₹${formatCurrency(parseFloat(totalAB))}`,
      },
      {
        title: `CGST Tax @${tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find(b => b.chargeType === "gst")
          ?.meta?.breakup?.find(t => t.name === "cgst")?.unitValue || 0}%`,
        value: `₹${formatNumber(tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find(b => b.chargeType === "gst")
          ?.meta?.breakup?.find(t => t.name === "cgst")?.totalCharge || 0)}`,
      },
      {
        title: `SGST Tax @${tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find(b => b.chargeType === "gst")
          ?.meta?.breakup?.find(t => t.name === "sgst")?.unitValue || 0}%`,
        value: `₹${formatNumber(tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find(b => b.chargeType === "gst")
          ?.meta?.breakup?.find(t => t.name === "sgst")?.totalCharge || 0)}`,
      },
      {
        title: "Total Tax Amt",
        value: `₹${formatNumber(tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find(b => b.chargeType === "gst")?.addedCharge || 0)}`,
      },
      {
        title: "Total Amount",
        bold:true,
        value: `₹${formatCurrency(parseFloat(tripDetails?.Payments?.[0]?.tripPricingDetails?.totalAmount || tripDetails?.price))}`
      },
    ],
    detailsArray8: [
      {
        title: "Gross Amount(A+B)",
        bold:true,
        value: `₹${formatCurrency(parseFloat(calculateSubTotalA(tripDetails) + calculateSubTotalB(tripDetails)))}`,
      },
      {
        title: `CGST Tax @${tripDetails?.Payments?.[0]?.cgst}%`,
        value: `₹${formatNumber(parseFloat(parseFloat(totalAB)*.01*parseFloat(tripDetails?.Payments?.[0]?.cgst)))}`,
      },
      {
        title: `SGST Tax @${tripDetails?.Payments?.[0]?.sgst}%`,
        value: `₹${(formatNumber(parseFloat(totalAB)*.01*parseFloat(tripDetails?.Payments?.[0]?.sgst)))}`,
      },
      {
        title: "Total Tax Amt",
        value: `₹${parseFloat(totalGstAmount)?.toFixed(2)}` || "",
      },
      {
        title: "Total Amount",
        bold:true,
        value: `₹${formatCurrency(parseFloat(tripDetails?.Payments?.[0]?.tripPricingDetails?.totalAmount || tripDetails?.price))}` || '',
      },
    ],
  };
};

//Consolidated invoice data
export const consolidateInvoiceData = (tripDetails) => {
  return {
    companyAckDetails: [
      {
        title: "IRN",
        value: tripDetails?.irn || "",
      },
      {
        title: "Ack No.",
        value: tripDetails?.ackNo || "",
      },
      {
        title: "Ack Date",
        value: tripDetails?.ackDate|| "",
      },
    ],
    companyCinDetails: [
      {
        title: "Submission City",
        value: tripDetails?.submissionCity || "",
      },
      {
        title: "CIN",
        value: tripDetails?.cin || "",
      },
      {
        title: "HSN/SAC Code",
        value: tripDetails?.hsnCode || "",
      },
    ],
    companyDetails: [
      {
        title: "Company Name",
        value: tripDetails?.companyName || "",
      },
      {
        title: "Company Address",
        value:  tripDetails?.companyAddress || "",
      },
    ],
    companyPanDetails: [
      {
        title: "PAN",
        value: tripDetails?.companyPan ||"",
      },
      {
        title: "Company GST No.",
        value: tripDetails?.companyGst || "",
      },
      {
        title: "POS",
        value: tripDetails?.pos || "",
      },
      {
        title: "Company Type",
        value:  tripDetails?.companyType || "",
      },
      {
        title: "Duties  Form",
        value: tripDetails?.dutiesFrom|| "",
      },
      {
        title: "Duties  To",
        value: tripDetails?.dutiesTo || "",
      },
    ],
    companyInvoiceDetails: [
      {
        title: "Invoice Date",
        value: tripDetails?.inoviceDate || "",
      },
      {
        title: "Invoice No.",
        value:  tripDetails?.inoviceId || "",
      },
      {
        title: "GST No.",
        value: tripDetails?.gstNo || "",
      },
      {
        title: "PAN",
        value: tripDetails?.panNo || "",
      },
    ],
  };
};

export const tripIndividualInvoiceData = (tripDetails, tripTypeDetails, subTotalA, subTotalB, totalAB, totalGstAmount) => {
  const tripPricing = tripDetails?.Payments?.[0]?.tripPricingDetails || {};
  const baseRate = parseFloat(tripPricing?.baseRate || 0);
  const surCharge = parseFloat(tripPricing?.surCharge || 0);
  const platformCharge = parseFloat(tripPricing?.platformCharge || 0);
  const extraKmCharges = tripDetails?.type === tripDutyTypeEnum?.rental 
    ? parseFloat(tripDetails?.extraKmCharges || 0)
    : parseFloat(tripDetails?.extraPrice || 0);
  const extraHrCharges = parseFloat(tripDetails?.extraHrCharges || 0);
  const breakdown = tripPricing?.breakdown || [];
  const tollCharges = parseFloat(breakdown.find(b => b.chargeType === "tollCharges")?.addedCharge || 0);
  const parkingCharges = parseFloat(breakdown.find(b => b.chargeType === "parkingCharge")?.addedCharge || 0);
  const driverAllowance = parseFloat(breakdown.find(b => b.chargeType === "driverAllowance")?.addedCharge || 0);
  const handlingCharges = parseFloat(tripDetails?.handlingCharges || 0);
  const nightAllowance = parseFloat(breakdown.find(b => b.chargeType === "nightAllowance")?.addedCharge || 0);
  const fuelSurcharge = parseFloat(tripDetails?.fuelSurcharge || 0);
  const interstateTax = parseFloat(tripDetails?.interstateTaxCharges || 0);
  const placardCharge = parseFloat(breakdown.find(b => b.chargeType === "placard")?.addedCharge || 0);
  const couponDiscount = Math.abs(parseFloat(breakdown.find(b => b.chargeType === "discountAmount")?.addedCharge || 0));
  
  // Build detailsArray4 with only non-zero values
  const detailsArray4Base = [
    {
      title: "Service Type",
      value: (  tripDetails?.dutyTypeName || "") + (tripDetails?.additionalInfo?.isMultiDay ? " (Multi-Day)" : ""),
    },
    {
      title: "Package",
      value: (tripDetails?.type === tripDutyTypeEnum.rental || tripDetails?.type === tripDutyTypeEnum.roundTrip) ? tripDetails?.Payments[0]?.tripPricingDetails?.rateConfig?.name : 'Per KM',
    },
    {
      title: "Package Rate",
      value: `₹${formatNumber(tripDetails?.Payments[0]?.tripPricingDetails?.baseFareDisplay || 0)}`,
    },
    {
      title: "km Used",
      value: `${formatNumber(parseFloat(tripDetails?.distanceActual || "0") + parseFloat(tripDetails?.extraKm || "0"))} `
    },
    ...(parseFloat(tripDetails?.extraKm) > 0 ? [{
      title: "Extra km",
      value: `${formatNumber(tripDetails?.extraKm) || ""}`,
    },
    {
      title: "Extra km Rs",
      value: tripDetails?.type === tripDutyTypeEnum?.rental ? `₹${formatNumber(tripDetails?.extraKmCharges)}` : `₹${formatNumber(tripDetails?.extraPrice)}`,
    }] : []),
    {
      title: "hr Used",
      value: convertDecimalHoursToTime(tripDetails?.totalTime) || "",
    },
    ...(parseFloat(tripDetails?.extraTime) > 0 ? [{
      title: "Extra hr",
      value: tripDetails?.extraTime ? parseFloat(tripDetails?.extraTime)?.toFixed(2) : "",
    },
    {
      title: "Extra hr Rs",
      value: tripDetails?.type === tripDutyTypeEnum?.rental ? `₹${tripDetails?.extraHrCharges}` : "0"
    }] : []),
    {
      title: "Car Booked",
      value: tripDetails?.MasterCarType?.carType || "",
    },
    {
      title: "Car Used On",
      value: tripDetails?.pickUpTime ? getDateFormat(tripDetails?.pickUpTime) :"",
    },
    {
      title: "Garage To Pickup",
      value: tripDetails?.Payments[0]?.tripPricingDetails?.kilometersOut || 'NA',
    },
    {
      title: "Drop To Garage",
      value: tripDetails?.Payments[0]?.tripPricingDetails?.kilometersIn || 'NA'
    },
    ...(baseRate > 0 ? [{
      title: "Base Rate",
      value: `₹${formatNumber(baseRate)}`,
    }] : []),
    ...(surCharge > 0 ? [{
      title: "Surcharge",
      value: `₹${formatNumber(surCharge)}`,
    }] : []),
    ...(platformCharge > 0 ? [{
      title: "Platform Charge",
      value: `₹${formatNumber(platformCharge)}`,
    }] : []),
  ];

  // Sub Total A summary section
  const detailsArray4Summary = [
    {
      title: "",
      value: "",
    },
    {
      title: "",
      value: "",
    },
    {
      title: "Sub Total(A)",
      bold: true,
      value: `₹${formatCurrency((calculateSubTotalA(tripDetails) || 0))}` || "",
    },
  ];

  // Build detailsArray5 with only non-zero values
  const detailsArray5Base = [
    ...(tollCharges > 0 ? [{
      title: "Toll Charges",
      value: `₹${formatNumber(tollCharges)}`,
    }] : []),
    ...(parkingCharges > 0 ? [{
      title: "Parking Charges",
      value: `₹${formatNumber(parkingCharges)}`,
    }] : []),
    ...(driverAllowance > 0 ? [{
      title: "Driver Allowance",
      value: `₹${formatNumber(driverAllowance)}` || "",
    }] : []),
    ...(handlingCharges > 0 ? [{
      title: "Additional Charges",
      value: `₹${formatNumber(handlingCharges)}` || "",
    }] : []),
    // {
    //   title: "Discount",
    //   value: `₹0.00`,
    // },
    ...(nightAllowance > 0 ? [{
      title: "Night Allowance",
      value: `₹${formatNumber(nightAllowance)}`,
    }] : []),
    ...(fuelSurcharge > 0 ? [{
      title: "Fuel Surcharge",
      value: `₹${formatNumber(fuelSurcharge)}` || "",
    }] : []),
    ...(interstateTax > 0 ? [{
      title: "Interstate Tax",
      value: `₹${formatNumber(interstateTax)}`,
    }] : []),
    ...(placardCharge > 0 ? [{
      title: "Placard Charge",
      value: `₹${formatNumber(placardCharge)}`,
    }] : []),
        ...(getGarageCharges(tripDetails) > 0 ? [{
      title: "Garage to Pickup Charge",
      value: `₹${formatNumber(parseFloat(breakdown.find(b => b.chargeType === 'garageCharge')?.meta?.breakup?.[0]?.rate || 0))}`,
    },
    {
      title: "Drop to Garage Charge",
      value: `₹${formatNumber(parseFloat(breakdown.find(b => b.chargeType === 'garageCharge')?.meta?.breakup?.[1]?.rate || 0))}`,
    }] : []),
    ...(couponDiscount > 0 ? [{
      title: "Coupon Discount",
      value: `-₹${formatNumber(couponDiscount)}`,
    }] : []),
  ];

  // Sub Total B summary section
  const detailsArray5Summary = [
    {
      title: "",
      value: "",
    },
    {
      title: "",
      value: "",
    },
    {
      title: "Sub Total (B)",
      bold: true,
      value: `₹${formatCurrency(parseFloat(calculateSubTotalB(tripDetails) || 0))}` || "",
    },
  ];

  return {
    companyAckDetails:[
      {
        title:"IRN",
        value:""
      },
      {
        title:"Ack No.",
        value:""
      },
      {
        title:"Ack Date",
        value:""
      }
    ],
    companyGstDetails1: [
      {
        title: "GSTIN",
        value: tripDetails?.tripBranch?.gstNumber || "",
      },
      {
        title: "PAN",
        value: tripDetails?.tripCompany?.panNumber || "",
      },
    ],

    companyDetails2: [
      // {
      //   title: "HSN/SAC Code",
      //   value: "",
      // },
      // {
      //   title: "Rental Type",
      //   value: "",
      // }
    ],
  detailsArray1: [
    {
      title: "Company Name",
      value: tripDetails?.companyName || "",
    },
    // {
    //   title: "Company Code",
    //   value: "",
    // },
    {
      title: "Company Address",
      value:  tripDetails?.tripCompany?.address || "",
    },
    {
      title: "Passenger Name",
      value: tripDetails?.customerName || "",
    },
    {
      title: "Booked By",
      value: tripDetails?.TripBookedBy?.name || "",
    },
    {
      title: "GSTIN of Company",
      value: tripDetails?.tripBranch?.gstNumber || tripDetails?.tripCompany?.gstNumber || "",
    },
    {
      title: "Travel Request No.",
      value: tripDetails?.travelRequestNumber || "",
    },
    {
      title: "PAN",
      value: tripDetails?.tripCompany?.panNumber || "",
    },
  ],
  detailsArray2: [
    {
      title: "Billing Type",
      value:
        Number(tripDetails?.additionalInfo?.billingType) ===
        billingTypeEnum?.billToCreditCard
          ? "Bill To Credit Card"
          : Number(tripDetails?.additionalInfo?.billingType) ===
            billingTypeEnum?.billToCompany
          ? "Bill To Company"
          : "",
    },
    {
      title: "DS No. / Booking ID",
      value: tripDetails?.tripId || "",
    },
    {
      title: "Invoice Date",
      value: getDateFormat(tripDetails?.Invoices?.[0]?.createdAt) || "",
    },
    {
      title: "Invoice No.",
      value: "",
    },
    {
      title: "Submission City",
      value: tripDetails?.ServiceCity?.name || "",
    },
    {
      title: "Place Of Supply",
      value: tripDetails?.tripBranch?.gstNumber || tripDetails?.tripBranch?.state,
    },
  ],
  detailsArray3: [
    {
      title: "Date Out",
      value: tripDetails?.pickUpTime ? getDateFormat(tripDetails?.pickUpTime) : "",
    },
    {
      title: "Date In",
      value: tripDetails?.completedAt ? getDateFormat(tripDetails?.completedAt) : "",
    },
    {
      title: "Start km",
      value: 0,
    },
    {
      title: "Time Out",
      value: getTimeFormat(tripDetails?.pickUpTime) || "",
    },
    {
      title: "Time In",
      value: getTimeFormat(tripDetails?.completedAt) || "",
    },
    {
      title: "End km",
      value: `${tripDetails?.distanceActual || ""}`,
    },
    {
      title: "Driver Name",
      value: tripDetails?.DriverProfile?.driverName || "",
    },
    {
      title: "Driver Phone No.",
      value: tripDetails?.DriverProfile?.mobile || "",
    },
    {
      title: "Reporting Time",
      value: getTimeFormat(tripDetails?.scheduledTime) || "",
    },
    {
      title: "Car No.",
      value: tripDetails?.DriverProfile?.Vehicle?.regNumber || "",
    },
    {
      title: "Car Alloted",
      value: tripDetails?.DriverProfile?.Vehicle?.carType || "",
    },
  ],
  detailsArray4: detailsArray4Base,
  detailsArray4Summary: detailsArray4Summary,
  detailsArray5: detailsArray5Base,
  detailsArray5Summary: detailsArray5Summary,
  detailsArray6: tripDetails?.termsAndConditions,
  detailsArray7: [
    {
      title: "Gross Amount(A+B)",
      bold:true,
      value: `₹${formatCurrency(parseFloat(calculateSubTotalA(tripDetails) + calculateSubTotalB(tripDetails)))}`,
    },
    {
      title: `CGST Tax @${formatNumber(tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find(b => b.chargeType === "gst")
        ?.meta?.breakup?.find(t => t.name === "cgst")?.unitValue || 0)}%`,
      value: `₹${formatNumber(tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find(b => b.chargeType === "gst")
        ?.meta?.breakup?.find(t => t.name === "cgst")?.totalCharge || 0)}`,
    },
    {
      title: `SGST Tax @${tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find(b => b.chargeType === "gst")
        ?.meta?.breakup?.find(t => t.name === "sgst")?.unitValue || 0}%`,
      value: `₹${tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find(b => b.chargeType === "gst")
        ?.meta?.breakup?.find(t => t.name === "sgst")?.totalCharge || 0}`,
    },
    {
      title: "Total Tax Amt",
      value: `₹${tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find(b => b.chargeType === "gst")?.addedCharge || ""}`,
    },
    {
      title: "Total Amount",
      bold:true,
      value: `₹${formatCurrency(parseFloat(tripDetails?.Payments?.[0]?.tripPricingDetails?.totalAmount || tripDetails?.price))}`
    },
  ],
  detailsArray8: [
    {
      title: "Gross Amount(A+B)",
      bold:true,
      value: `₹${formatCurrency(parseFloat(totalAB))}`,
    },
    {
      title: `CGST Tax @${tripDetails?.Payments?.[0]?.cgst}%`,
      value: `₹${formatNumber(parseFloat(parseFloat(totalAB)*.01*parseFloat(tripDetails?.Payments?.[0]?.cgst)) || 0)}`,
    },
    {
      title: `SGST Tax @${tripDetails?.Payments?.[0]?.sgst}%`,
      value: `₹${(formatNumber(parseFloat(totalAB)*.01*parseFloat(tripDetails?.Payments?.[0]?.sgst)) || 0)}`,
    },
    {
      title: "Total Tax Amt",
      value: `₹${formatNumber(parseFloat(totalGstAmount) || 0)}` || "",
    },
    {
      title: "Total Amount",
      bold:true,
      value: `₹${formatCurrency(parseFloat(tripDetails?.Payments?.[0]?.tripPricingDetails?.totalAmount || tripDetails?.price) || 0)}`,
    },
  ],
};
};